import type {
  AdminLicenseComplianceDto,
  UserComplianceSummaryDto,
} from '../../services/complianceReportService';
import {
  getLicenseStanding,
  getMemberStanding,
  isAdminLicenseAtRisk,
  isLicenseSetUp,
} from './licenseStatus';

/**
 * Firm-wide rollup aggregations for the Team Reporting overview band.
 * Everything derives client-side from the already-fetched compliance report;
 * no additional API calls.
 */

/**
 * Mutually exclusive member bucket, worst-first. Unlike MemberStanding (a
 * status plus overlays), every member lands in exactly one bucket so segment
 * counts always sum to the member total and match the filtered table rows.
 */
export type MemberBucket = 'attention' | 'at_risk' | 'in_progress' | 'concluded' | 'not_set_up';

/**
 * When `stateCode` is given, standing derives from that jurisdiction's licenses
 * only. This keeps the standing buckets honest while a state filter is active:
 * "TX + Attention Required" means the TX license needs attention, not that some
 * other state's license dragged the member into the bucket.
 */
export function getMemberBucket(
  member: UserComplianceSummaryDto,
  now: Date = new Date(),
  stateCode?: string
): MemberBucket {
  const scoped = stateCode
    ? { ...member, licenses: (member.licenses ?? []).filter((l) => l.stateCode === stateCode) }
    : member;
  const standing = getMemberStanding(scoped, now);
  if (standing.attentionRequired) return 'attention';
  if (standing.atRisk) return 'at_risk';
  if (standing.status === 'concluded') return 'concluded';
  if (!(scoped.licenses ?? []).some(isLicenseSetUp)) return 'not_set_up';
  return 'in_progress';
}

export interface StandingCounts {
  attention: number;
  atRisk: number;
  inProgress: number;
  concluded: number;
  notSetUp: number;
  total: number;
}

/**
 * With `stateCode`, only members holding a license in that jurisdiction are
 * counted, and each member's bucket derives from those licenses alone, so the
 * standing bar's segment counts keep matching the state-filtered table rows.
 */
export function getStandingCounts(
  users: UserComplianceSummaryDto[],
  now: Date = new Date(),
  stateCode?: string
): StandingCounts {
  const scoped = stateCode
    ? users.filter((u) => (u.licenses ?? []).some((l) => l.stateCode === stateCode))
    : users;
  const counts: StandingCounts = {
    attention: 0,
    atRisk: 0,
    inProgress: 0,
    concluded: 0,
    notSetUp: 0,
    total: scoped.length,
  };
  for (const user of scoped) {
    switch (getMemberBucket(user, now, stateCode)) {
      case 'attention':
        counts.attention += 1;
        break;
      case 'at_risk':
        counts.atRisk += 1;
        break;
      case 'concluded':
        counts.concluded += 1;
        break;
      case 'not_set_up':
        counts.notSetUp += 1;
        break;
      default:
        counts.inProgress += 1;
    }
  }
  return counts;
}

/** Per-license standing split used by the horizon and jurisdiction visuals. */
export interface LicenseStandingSplit {
  attention: number;
  atRisk: number;
  onPace: number;
  concluded: number;
  total: number;
}

function emptySplit(): LicenseStandingSplit {
  return { attention: 0, atRisk: 0, onPace: 0, concluded: 0, total: 0 };
}

function addLicenseToSplit(
  split: LicenseStandingSplit,
  license: AdminLicenseComplianceDto,
  now: Date,
  memberLicenses?: AdminLicenseComplianceDto[]
): void {
  const standing = getLicenseStanding(license, now, memberLicenses);
  if (standing === 'not_set_up') return;
  if (standing === 'attention') split.attention += 1;
  else if (standing === 'concluded') split.concluded += 1;
  else if (isAdminLicenseAtRisk(license, now, memberLicenses)) split.atRisk += 1;
  else split.onPace += 1;
  split.total += 1;
}

export interface DeadlineMonth extends LicenseStandingSplit {
  /** UTC year-month key, e.g. "2026-09". */
  key: string;
  /** Short month label, e.g. "Sep". */
  label: string;
  /** Two-digit year suffix, e.g. "'27" (shown on January and the first labeled column). */
  yearLabel: string;
}

/**
 * Set-up licenses bucketed by reporting-period end month (UTC) over the next
 * `months` months, starting with the current month.
 */
export function getDeadlineHorizon(
  users: UserComplianceSummaryDto[],
  now: Date = new Date(),
  months = 12
): DeadlineMonth[] {
  const horizon: DeadlineMonth[] = [];
  const byKey = new Map<string, DeadlineMonth>();
  for (let i = 0; i < months; i++) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
    const key = date.toISOString().slice(0, 7);
    const month: DeadlineMonth = {
      key,
      label: date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }),
      yearLabel: `'${String(date.getUTCFullYear()).slice(2)}`,
      ...emptySplit(),
    };
    horizon.push(month);
    byKey.set(key, month);
  }
  for (const user of users) {
    for (const license of user.licenses ?? []) {
      if (!isLicenseSetUp(license) || !license.periodEnd) continue;
      const month = byKey.get(license.periodEnd.slice(0, 7));
      if (month) addLicenseToSplit(month, license, now, user.licenses);
    }
  }
  return horizon;
}

export interface StateMixRow extends LicenseStandingSplit {
  stateCode: string;
}

/**
 * Set-up licenses grouped by jurisdiction, sorted by volume (ties
 * alphabetical). States where no license is set up yet are omitted.
 */
export function getStateMix(
  users: UserComplianceSummaryDto[],
  now: Date = new Date()
): StateMixRow[] {
  const byState = new Map<string, StateMixRow>();
  for (const user of users) {
    for (const license of user.licenses ?? []) {
      if (!isLicenseSetUp(license)) continue;
      let row = byState.get(license.stateCode);
      if (!row) {
        row = { stateCode: license.stateCode, ...emptySplit() };
        byState.set(license.stateCode, row);
      }
      addLicenseToSplit(row, license, now, user.licenses);
    }
  }
  return [...byState.values()].sort(
    (a, b) => b.total - a.total || a.stateCode.localeCompare(b.stateCode)
  );
}

/**
 * A license "tracks ethics" when its confirmed requirements include an ethics
 * hour target OR any ethics-typed subject/manual requirement (state-specific
 * ethics in manual-attestation states like TN carry no headline hour target).
 * Licenses tracking neither never count — states may have no ethics rule and
 * we never imply a requirement that isn't configured.
 */
export function isLicenseEthicsTracked(license: AdminLicenseComplianceDto): boolean {
  return (
    isLicenseSetUp(license) &&
    ((license.ethicsTargetHours ?? 0) > 0 ||
      license.subjectRequirements.some((r) => r.categoryType === 'ethics'))
  );
}

/**
 * "Ethics short" = earned ethics hours under the target, OR any ethics-typed
 * subject/manual requirement still unmet. States with both (e.g. AZ) must
 * satisfy both to clear.
 */
export function isLicenseEthicsShort(license: AdminLicenseComplianceDto): boolean {
  if (!isLicenseEthicsTracked(license)) return false;
  return (
    (license.ethicsEarnedHours ?? 0) < (license.ethicsTargetHours ?? 0) ||
    license.subjectRequirements.some((r) => r.categoryType === 'ethics' && !r.isMet)
  );
}

export interface EthicsGapRow {
  stateCode: string;
  /** Licenses short on ethics that are already inside the attention window. */
  attention: number;
  /** Licenses short on ethics but not yet urgent. */
  atRisk: number;
  short: number;
  /** Set-up licenses in this jurisdiction tracking ethics (hour target or ethics-typed requirement). */
  tracked: number;
}

export interface EthicsCoverageRollup {
  /** Jurisdictions with at least one shortfall, worst-first (ties alphabetical). */
  rows: EthicsGapRow[];
  shortTotal: number;
  /** Set-up licenses tracking ethics (hour target or ethics-typed requirement). */
  trackedTotal: number;
}

/** Firm-wide ethics exposure: tracked ethics targets vs. licenses still short. */
export function getEthicsCoverage(
  users: UserComplianceSummaryDto[],
  now: Date = new Date()
): EthicsCoverageRollup {
  const byState = new Map<string, EthicsGapRow>();
  const trackedByState = new Map<string, number>();
  let shortTotal = 0;
  let trackedTotal = 0;
  for (const user of users) {
    for (const license of user.licenses ?? []) {
      if (!isLicenseEthicsTracked(license)) continue;
      trackedTotal += 1;
      trackedByState.set(license.stateCode, (trackedByState.get(license.stateCode) ?? 0) + 1);
      if (!isLicenseEthicsShort(license)) continue;
      shortTotal += 1;
      let row = byState.get(license.stateCode);
      if (!row) {
        row = { stateCode: license.stateCode, attention: 0, atRisk: 0, short: 0, tracked: 0 };
        byState.set(license.stateCode, row);
      }
      if (getLicenseStanding(license, now, user.licenses) === 'attention') row.attention += 1;
      else row.atRisk += 1;
      row.short += 1;
    }
  }
  for (const row of byState.values()) {
    row.tracked = trackedByState.get(row.stateCode) ?? 0;
  }
  const rows = [...byState.values()].sort(
    (a, b) => b.short - a.short || a.stateCode.localeCompare(b.stateCode)
  );
  return { rows, shortTotal, trackedTotal };
}
